import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Toaster, toast } from 'react-hot-toast';
import {
  AlertCircle,
  Check,
  Globe,
  Lock,
  Shield,
  Info,
  Save,
  Building2,
  Zap,
  Edit3
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../utils/api';

// ——————————————— Styled Components ———————————————

const PageWrapper = styled.div`
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  min-height: 100vh;
  padding: 0;
  margin: 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  background: white;
  min-height: 100vh;
`;

const Header = styled.div`
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  padding: 2rem 3rem;
  color: white;
  position: relative;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  color: white;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
  max-width: 800px;
  margin: 0 auto;
  font-weight: 400;
`;

const FeatureHighlight = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  
  svg {
    margin-right: 0.75rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  background: white;
  position: relative;
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: calc(100vh - 200px);
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  padding: 4rem 3rem;
  background: ${props => props.primary ? 'white' : '#fafbfc'};
  border-right: ${props => props.primary ? '1px solid #e5e7eb' : 'none'};
  position: relative;
  
  @media (max-width: 1200px) {
    border-right: none;
    border-bottom: ${props => props.primary ? '1px solid #e5e7eb' : 'none'};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.primary ? 
      'linear-gradient(90deg, #00419f, #1562b7, #1e90ff)' : 
      'linear-gradient(90deg, #64748b, #475569, #334155)'};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;

  svg {
    margin-right: 1rem;
    color: ${props => props.primary ? '#00419f' : '#64748b'};
    width: 22px;
    height: 22px;
  }
`;

const SectionDescription = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: ${props => props.cols === 2 ? '1fr 1fr' : '1fr'};
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.mb || '2rem'};
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
`;

const RequiredIndicator = styled.span`
  color: #ef4444;
  margin-left: 0.25rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  color: #1f2937;
  transition: all 0.3s ease;
  background: white;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${props => props.error && `
    border-color: #ef4444;
    background-color: #fef2f2;
    
    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  min-height: 120px;
  resize: vertical;
  font-size: 0.875rem;
  color: #1f2937;
  transition: all 0.3s ease;
  background: white;
  font-family: inherit;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
`;

const UrlInputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
`;

const ProtocolSelect = styled(Select)`
  width: 140px;
  flex-shrink: 0;
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-left: 0.5rem;
  font-weight: 500;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 3rem;
  display: flex;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #00419f, #1562b7, #1e90ff);
  }
`;

const InfoIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(0, 65, 159, 0.3);
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #00419f;
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
`;

const InfoText = styled.p`
  color: #1562b7;
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;
`;

const ValidationSummary = styled.div`
  background: ${props => props.valid ? 
    'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : 
    'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'};
  border: 1px solid ${props => props.valid ? '#bbf7d0' : '#fecaca'};
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
`;

const ValidationIcon = styled.div`
  margin-right: 1rem;
  color: ${props => props.valid ? '#22c55e' : '#ef4444'};
`;

const ValidationText = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.valid ? '#166534' : '#dc2626'};
`;

const ActionBar = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-top: 1px solid #e5e7eb;
  padding: 2rem 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 65, 159, 0.2), transparent);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 3.5rem;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  ${props =>
    props.primary &&
    `
    background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
    color: white;
    border: none;
    box-shadow: 0 4px 6px -1px rgba(0, 65, 159, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(0, 65, 159, 0.4);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  `}

  ${props =>
    props.secondary &&
    `
    background: white;
    color: #64748b;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  `}

  svg {
    margin-right: 0.75rem;
  }
`;

const CurrentValueCard = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #f59e0b, #d97706, #92400e);
  }
`;

const CurrentValueIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(180deg, #f59e0b, #d97706);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(245, 158, 11, 0.3);
`;

const CurrentValueContent = styled.div`
  flex: 1;
`;

const CurrentValueTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 0.25rem;
  letter-spacing: -0.01em;
`;

const CurrentValueText = styled.p`
  color: #d97706;
  font-size: 0.8rem;
  line-height: 1.4;
  margin: 0;
  font-weight: 500;
`;

// ——————————————— Component Logic ———————————————

function EditAssetsWeb() {
  const navigate = useNavigate();
  const location = useLocation();
  const { asset } = location.state || {};

  const [assetData, setAssetData] = useState({
    oldName: asset?.name || '',
    newName: asset?.name || '',
    url: asset?.url || '',
    protocol: asset?.url?.startsWith('http://') ? 'http://' : 'https://',
    domain: asset?.url?.replace(/^https?:\/\//, '') || '',
    type: 'Web Application',
    backend: asset?.backend || '',
    loginUrl: asset?.loginUrl || '',
    username: asset?.username || '',
    password: asset?.password || '',
    cookies: asset?.cookies || ''
  });

  const [validation, setValidation] = useState({
    step1Valid: false,
    step2Valid: true // Authentication is optional
  });

  // Update URL when protocol or domain changes
  useEffect(() => {
    setAssetData(prev => ({
      ...prev,
      url: prev.protocol + prev.domain
    }));
  }, [assetData.protocol, assetData.domain]);

  // Validate forms
  useEffect(() => {
    const step1Valid = 
      assetData.newName.trim() !== '' && 
      assetData.domain.trim() !== '' && 
      assetData.type !== '';

    setValidation(prev => ({
      ...prev,
      step1Valid
    }));
  }, [assetData]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setAssetData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = () => {
    if (!validation.step1Valid) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    const assetType = 'website';
    const body = {
      oldName: assetData.oldName,
      newName: assetData.newName,
      assetType,
      url: assetData.url,
      backend: assetData.backend,
      loginSequence: {
        loginUrl: assetData.loginUrl,
        username: assetData.username,
        password: assetData.password,
        cookies: assetData.cookies
      },
      ips: []
    };

    apiRequest('/api/assets/edit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          toast.success(data.message || 'Asset updated successfully!');
          navigate('/add-assets');
        } else {
          throw new Error(data.message || 'Failed to update asset');
        }
      })
      .catch(error => {
        console.error('Error updating asset:', error);
        toast.error(error.message || 'An error occurred while updating the asset');
      });
  };

  return (
    <PageWrapper>
      <Container>
        {/* <Header>
          <HeaderContent>
            <Title>Edit Web Application Asset</Title>
            <Subtitle>
              Update your web application configuration with enhanced security and performance 
              optimization. Modify asset details and authentication settings with enterprise-grade reliability.
            </Subtitle>
            <FeatureHighlight>
              <FeatureItem>
                <Shield size={20} />
                Secure Updates
              </FeatureItem>
              <FeatureItem>
                <Zap size={20} />
                Instant Changes
              </FeatureItem>
              <FeatureItem>
                <Building2 size={20} />
                Enterprise Ready
              </FeatureItem>
            </FeatureHighlight>
          </HeaderContent>
        </Header> */}

        <MainContent>
          <FormContainer>
            {/* Asset Details Section */}
            <FormSection primary>
              <SectionHeader>
                <SectionTitle primary>
                  <Edit3 size={28} />
                  Asset Configuration
                </SectionTitle>
                <SectionDescription>
                  Update your web application details and technical specifications. 
                  Modify asset information while maintaining security compliance and operational integrity.
                </SectionDescription>
              </SectionHeader>

              <CurrentValueCard>
                <CurrentValueIcon>
                  <Info size={16} />
                </CurrentValueIcon>
                <CurrentValueContent>
                  <CurrentValueTitle>Current Asset Name</CurrentValueTitle>
                  <CurrentValueText>
                    Editing: {assetData.oldName || 'Unnamed Asset'}
                  </CurrentValueText>
                </CurrentValueContent>
              </CurrentValueCard>

              <InfoCard>
                <InfoIcon>
                  <Shield size={24} />
                </InfoIcon>
                <InfoContent>
                  <InfoTitle>Secure Asset Management</InfoTitle>
                  <InfoText>
                    Your asset modifications are protected with end-to-end encryption and logged 
                    for audit compliance. All changes are validated and stored securely with 
                    industry-standard SOC 2 Type II controls.
                  </InfoText>
                </InfoContent>
              </InfoCard>

              <ValidationSummary valid={validation.step1Valid}>
                <ValidationIcon valid={validation.step1Valid}>
                  {validation.step1Valid ? <Check size={20} /> : <AlertCircle size={20} />}
                </ValidationIcon>
                <ValidationText valid={validation.step1Valid}>
                  {validation.step1Valid 
                    ? 'All required configuration fields are completed' 
                    : 'Please complete all required fields with valid information'}
                </ValidationText>
              </ValidationSummary>

              <FormGrid cols={2}>
                <FormGroup>
                  <Label>
                    Current Name (Read-Only)
                  </Label>
                  <Input
                    type="text"
                    value={assetData.oldName}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    New Asset Name <RequiredIndicator>*</RequiredIndicator>
                  </Label>
                  <Input
                    type="text"
                    name="newName"
                    value={assetData.newName}
                    onChange={handleInputChange}
                    placeholder="Updated Production Website, New Admin Portal"
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <Label>
                  Application URL <RequiredIndicator>*</RequiredIndicator>
                </Label>
                <UrlInputGroup>
                  <ProtocolSelect
                    name="protocol"
                    value={assetData.protocol}
                    onChange={handleInputChange}
                  >
                    <option value="https://">HTTPS</option>
                    <option value="http://">HTTP</option>
                  </ProtocolSelect>
                  <Input
                    type="text"
                    name="domain"
                    value={assetData.domain}
                    onChange={handleInputChange}
                    placeholder="app.company.com"
                  />
                </UrlInputGroup>
              </FormGroup>

              <FormGroup>
                <Label>
                  Asset Type
                </Label>
                <Input
                  type="text"
                  name="type"
                  value="Web Application"
                  readOnly
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </FormGroup>

              <FormGroup mb="0">
                <Label>
                  Backend Technology
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                </Label>
                <Select
                  name="backend"
                  value={assetData.backend}
                  onChange={handleInputChange}
                >
                  <option value="">Select backend technology</option>
                  <option value="php">PHP</option>
                  <option value="nodejs">Node.js</option>
                  <option value="django">Django</option>
                  <option value="flask">Flask</option>
                  <option value="asp.net">ASP.NET</option>
                  {/* <option value="ruby">Ruby on Rails</option>
                  <option value="java">Java (Spring)</option>
                  <option value="python">Python</option>
                  <option value="other">Other</option> */}
                </Select>
              </FormGroup>
            </FormSection>

            {/* Authentication Section */}
            <FormSection>
              <SectionHeader>
                <SectionTitle>
                  <Lock size={28} />
                  Authentication Configuration
                </SectionTitle>
                <SectionDescription>
                  Update authentication credentials and access configurations for enhanced 
                  security scanning and automated vulnerability assessment capabilities.
                </SectionDescription>
              </SectionHeader>

              <InfoCard>
                <InfoIcon>
                  <Lock size={24} />
                </InfoIcon>
                <InfoContent>
                  <InfoTitle>Enhanced Credential Security</InfoTitle>
                  <InfoText>
                    Updated authentication details are encrypted using military-grade AES-256 
                    encryption and stored in hardened security vaults. All credential access 
                    is monitored and requires multi-factor authentication.
                  </InfoText>
                </InfoContent>
              </InfoCard>

              <FormGroup>
                <Label>Login URL</Label>
                <Input
                  type="text"
                  name="loginUrl"
                  value={assetData.loginUrl}
                  onChange={handleInputChange}
                  placeholder="https://app.company.com/login"
                />
              </FormGroup>

              <FormGrid cols={2}>
                <FormGroup>
                  <Label>Username / Email</Label>
                  <Input
                    type="text"
                    name="username"
                    value={assetData.username}
                    onChange={handleInputChange}
                    placeholder="admin@company.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    name="password"
                    value={assetData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••••••"
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup mb="0">
                <Label>
                  Session Cookies
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                </Label>
                <TextArea
                  name="cookies"
                  value={assetData.cookies}
                  onChange={handleInputChange}
                  placeholder="session_id=abc123; csrf_token=xyz789; user_preferences=dark_mode"
                />
              </FormGroup>
            </FormSection>
          </FormContainer>

          <ActionBar>
            <Button secondary onClick={() => navigate('/add-assets')}>
              Cancel Changes
            </Button>
            <Button 
              primary 
              onClick={handleSaveChanges}
              disabled={!validation.step1Valid}
            >
              <Save size={20} />
              Update Asset
            </Button>
          </ActionBar>
        </MainContent>
      </Container>

      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'white',
            color: '#1e293b',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            fontWeight: '500',
          },
        }}
      />
    </PageWrapper>
  );
}

export default EditAssetsWeb;